
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, InputAdornment, styled } from '@mui/material';
import FollowUpApi from 'api/follow-up.api';
import ClinicApi from 'api/master/clinic.api';
import IconNewEdit from 'assets/icon/newEdit';
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker';
import { notiError } from 'component/notifications/notifications';

import TableCustom, { TablePaginationMiniCustom } from 'component/Table/TableCustom';
import TableRowCommon from 'component/Table/TableRowCommon';
import { colors } from 'constants/theme';
import _ from 'lodash';
import moment from 'moment';
import MainButton from 'new-components/buttons/main-button';
import SelectInput from 'new-components/inputs/select-input';
import TextInput from 'new-components/inputs/text-input';
import PageModal from 'new-components/modal/page-modal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBranch } from 'utils/app.utils';
import { dateToView } from 'utils/date.utils';
import UseWindowSize from 'utils/useWindowSize';
import { StatusSelectorCustom } from './follow-up';

export interface IViewHistoryProps {
  onClose: () => void;
  onEdit?: (followUpId: number, isEdit: boolean) => void;
  disabled?: boolean
}

const TableConfig = styled('div')({
  '.MuiTableContainer-root': {
    maxHeight: 'calc(56px + 10 * 52px) !important',
    overflowY: 'auto !important',
  },
  '.MuiPaper-root table thead': {
    backgroundColor: `${colors.themeMainColor} !important`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
});

const THead = styled('div')({
  display: 'flex',
  backgroundColor: colors.themeMainColor,
  padding: '0.5rem 1.5rem',
  color: colors.white,
});

const TBody = styled('div')({
  border: `1px solid ${colors.lightGray}`,
  borderTopWidth: 0,
  padding: '0.625rem 1.5rem',
});

const Cell = styled('div')({ '&:first-of-type': { marginRight: '1.25rem', width: '100%' } });

export default function ViewHistoryFollowUp(props: IViewHistoryProps) {
  const { t } = useTranslation();
  const [width] = UseWindowSize();
  const [caseHistoryData, setCaseHistoryData] = useState<any[]>([]);
  const initialDateRange: [string, string] = [moment().format('YYYY-MM-DD'), moment().add(3, 'months').format('YYYY-MM-DD')];
  const [rangeDate, setRangeDate] = useState<any>(['', ''])
  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(width > 1200 ? 3 : 10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('date')
  const [sortType, setSortType] = useState('DESC')
  const [status, setStatus] = useState<boolean>(true)

  /** FILTER */
  const [search, setSearch] = useState('')
  const [fetch, setFetch] = useState<boolean>(false);
  const [conditionLoad, setConditionLoad] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    branchId: getBranch(),
    rangeDate: ['', ''],
  })



  const verifyPermission = async () => {
    ClinicApi.findAllBranches()
      .then(({ data }: any) => setBranches(data))
      .catch((e) => {
        return
      })

  }


  useEffect(() => {
    verifyPermission()
  }, [branchId])


  const loadDataHistoryStage = async () => {
    try {
      let condition: any = {}
      if (page) condition = { ...condition, page: page }
      if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
      if (appliedFilters.branchId) condition = { ...condition, branchId: appliedFilters.branchId }
      if (appliedFilters.search) condition = { ...condition, search: appliedFilters.search }
      if (appliedFilters.rangeDate) {
        if ((appliedFilters.rangeDate[0] !== null || appliedFilters.rangeDate[0] !== '') && (appliedFilters.rangeDate[1] !== null || appliedFilters.rangeDate[1] !== ''))
          condition = { ...condition, dateStart: appliedFilters.rangeDate[0], dateEnd: appliedFilters.rangeDate[1] }
      }
      await FollowUpApi.historyFollowUp(condition).then(({ data, headers }) => {
        if (data) {
          setRowCount(headers['x-total'])
          setCaseHistoryData(data)
          if (fetch || status) {
            setConditionLoad(condition)
          }
          setStatus(false)
        }
      }).finally(() => {
        setFetch(false)
      })
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadDataHistoryStage()
  }, [page, pageLimit, sortBy, sortType, appliedFilters])


  const renderData = (item: any, idx: number) => {
    const no = (page - 1) * pageLimit + idx + 1;
    const objRenderData = {
      key: String(item.index),
      id: String(item.index),
      obj: item,
      columns: [
        { option: 'TEXT', label: no, align: 'center' },
        { option: 'TEXT', label: item.finishDate ? dateToView(item.finishDate) : '-', align: 'left' },
        { option: 'TEXT', label: item.lastFollowUp ? dateToView(item.lastFollowUp) : '-', align: 'left' },
        { option: 'TEXT', label: item?.cnNumber ?? '-', align: 'left' },
        { option: 'TEXT', label: item?.patientName ?? '-', align: 'left', style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
        { option: 'TEXT', label: item?.phone ?? '-', align: 'left' },
        { option: 'TEXT', label: item?.doctorName ?? '-', align: 'left', style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
        { option: 'TEXT', label: item?.treatmentCourseName ?? '-', width: 200, align: 'left' },
        { option: 'COMPONENT', align: 'left', width: 180, component: <StatusSelectorCustom status={item?.status} unshow /> },
        {
          option: 'COMPONENT',
          align: 'center',
          component: (
            <Box
              sx={{
                border: `2px solid ${props.disabled ? 'transparent' : colors.themeSecondColor}`,
                color: props.disabled ? '#808080 !important' : colors.themeSecondColor,
                background: props.disabled ? '#E0E0E0 !important' : 'transparent',
                borderRadius: 1,
                p: 0.5,
                cursor: props.disabled ? 'not-allowed' : 'pointer',
              }}
              onClick={() => !props.disabled && props.onEdit?.(item.followUpId, true)}>
              <IconNewEdit disabled={props.disabled} />
            </Box>
          ),
        },
      ]
    };
    return <TableRowCommon {...objRenderData} />
  };

  const headCells = [
    { id: 'no', label: t('ลำดับ') },
    { id: 'closeDate', label: t('วันที่ปิดเคส') },
    { id: 'followUp', label: t('	วันที่ติดตามล่าสุด') },
    { id: 'patientCode', label: t('รหัสผู้ป่วย ') },
    { id: 'patientName', label: t('ชื่อผู้ป่วย') },
    { id: 'phone', label: t('เบอร์มือถือ') },
    { id: 'dentist', label: t('ทันตแพทย์') },
    { id: 'nameCourse', label: t('ชื่อคอร์ส') },
    { id: 'satus', label: t('สถานะ') },
    { id: 'action', label: t('Action') },
  ];

  return <PageModal
    isOpen={true}
    size={'L'}
    title={t('ประวัติเคสที่จบแล้ว')}
    handleClose={props.onClose}
    onCloseSubmit={true}
    onSubmit={{ onClick: () => null }}
    onCancel={{ onClick: props.onClose }}
    overflow={true}
    sx={{ zIndex: 2000 }}
  >
    <>
      <Box sx={{ paddingY: '16px' }}>
        <Grid container spacing="8px " columns={24}>
          <Grid item xs={24} sm={12} md={6} xl={5}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => {
                setBranchId(Number(e.target.value));
              }}
              options={[
                ...branches.map((b: any) => ({
                  value: b.branchId,
                  name: b.branchName || b.branchNameEn || ''
                }))]}
              width="100%"
            />
          </Grid>
          <Grid item xs={24} sm={12} md={6} xl={5}>
            <TextInput
              label={t('ค้นหา ชื่อคอร์ส')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={24} sm={12} md={6} xl={5}>
            <InputSecondNewRangePicker
              inputHeight={41}
              value={rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              allowClear
              onClear={() => setRangeDate(emptyRangePicker)}
              label={`${t('ช่วงวันที่ติดตาม เริ่มต้น')}  »  ${t('สิ้นสุด')}`}
            />
          </Grid>
          <Grid item xs={24} sm={12} md={6} xl={9}>
            <MainButton
              onClick={() => {
                setFetch(true)
                setPage(1)
                setLoading(true)
                setAppliedFilters({
                  search: search,
                  branchId,
                  rangeDate: rangeDate,
                })
              }}
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
            />
          </Grid>
        </Grid>
      </Box>
      <div className="d-none d-xl-block">
        <TableConfig>
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            rowCount={rowCount}
            onSort={(sortByVal: string, sortTypeVal: string) => {
              setSortType(sortTypeVal)
              setSortBy(sortByVal)
            }}
            setPageLimit={setPageLimit}
            setPage={setPage}
            headCells={headCells}
            rowsData={_.map(caseHistoryData, (val, i) => renderData(val, i))}
          />
        </TableConfig>
      </div>
      <div className="d-block d-xl-none">
        <THead>
          <Cell>{t('#')}</Cell>
        </THead>
        <div className={'treatment-body'}>
          {_.map(caseHistoryData, renderData)}
          <div className={'mt-2'}>
            <TablePaginationMiniCustom
              count={rowCount || 0}
              page={page}
              rowsPerPage={pageLimit}
              rowCount={rowCount}
              pageLimit={pageLimit}
              headCells={headCells}
              onPageChange={(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)}
              setPageLimit={(event: any) => {
                setPageLimit(parseInt(event.target.value, 10))
                setPage(1)
              }}
              setPage={setPage}
            />
          </div>
        </div>
      </div>
    </>
  </PageModal >
}
